import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  getAllBrands,
  getAllCategories,
  getEstoqueDisponibilidadeRelatorio,
  getSubCategoriesByCategory,
} from '../../services/api';
import { getAuthPermissoes } from '../../services/authStorage';
import { useEstabelecimento } from '../../context/EstabelecimentoContext';
import PageHeader from '../common/PageHeader';
import ResponsiveTable from '../common/ResponsiveTable';
import SortableTh from '../common/SortableTh';
import { useClientSort } from '../../hooks/useClientSort';
import { getApiErrorMessage } from '../../utils/apiError';
import { normalizeSearchTerm } from '../../utils/productSearch';
import BuscaProdutoCampo from '../common/BuscaProdutoCampo';

const PAGE = 50;

function podeRelatorio(permissions) {
  if (permissions == null) return true;
  return Boolean(permissions.estoqueRelatorio || permissions.acessoGerencial);
}

function fmtQtd(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

function textoSaldo(row, local) {
  const chave = String(local.id);
  const saldos = row?.saldos || {};
  if (Object.prototype.hasOwnProperty.call(saldos, chave)) return fmtQtd(saldos[chave]);
  if (Number(local.estabelecimentoId) !== Number(row.estabelecimentoId)) return '—';
  return fmtQtd(0);
}

const filtrosVazios = {
  filialId: '',
  categoriaId: '',
  subCategoriaId: '',
  marcaId: '',
  termo: '',
};

export default function EstoqueDisponibilidadeReport() {
  const permissions = useMemo(() => getAuthPermissoes(), []);
  const { estabelecimentoId, estabelecimentos, carregando } = useEstabelecimento();

  const [filialId, setFilialId] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [subCategoriaId, setSubCategoriaId] = useState('');
  const [marcaId, setMarcaId] = useState('');
  const [termo, setTermo] = useState('');
  const [applied, setApplied] = useState(null);

  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], totalItems: 0, locais: [], variasFiliais: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const filiais = useMemo(
    () => (estabelecimentos || []).map((e) => ({
      id: e.id,
      nome: e.nomeFantasia || e.razaoSocial || `Filial ${e.id}`,
    })),
    [estabelecimentos],
  );

  useEffect(() => {
    if (applied || carregando) return;
    const id = estabelecimentoId ? String(estabelecimentoId) : '';
    setFilialId(id);
    setApplied({ ...filtrosVazios, filialId: id });
  }, [applied, carregando, estabelecimentoId]);

  useEffect(() => {
    const loadCombos = async () => {
      try {
        const [catResp, marcaResp] = await Promise.all([
          getAllCategories({ indice: 1, qtde: 300 }),
          getAllBrands({ indice: 1, qtde: 300 }),
        ]);
        setCategorias(catResp.data?.items || []);
        setMarcas(marcaResp.data?.items || []);
      } catch {
        /* combos opcionais */
      }
    };
    loadCombos();
  }, []);

  useEffect(() => {
    if (!categoriaId) {
      setSubcategorias([]);
      return;
    }
    let ativo = true;
    getSubCategoriesByCategory(categoriaId)
      .then(({ data: resp }) => {
        if (!ativo) return;
        const lista = Array.isArray(resp) ? resp : (resp?.items || []);
        setSubcategorias(lista);
      })
      .catch(() => {
        if (ativo) setSubcategorias([]);
      });
    return () => { ativo = false; };
  }, [categoriaId]);

  const carregar = useCallback(async () => {
    if (!applied) return;
    if (!podeRelatorio(permissions)) {
      setError('Sem permissão para relatórios de estoque.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data: resp } = await getEstoqueDisponibilidadeRelatorio({
        estabelecimentoId: applied.filialId || undefined,
        categoriaId: applied.categoriaId || undefined,
        subCategoriaId: applied.subCategoriaId || undefined,
        marcaId: applied.marcaId || undefined,
        termo: applied.termo || undefined,
        indice: page,
        qtde: PAGE,
      });
      setData({
        items: resp?.items || [],
        totalItems: resp?.totalItems || 0,
        locais: resp?.locais || [],
        variasFiliais: Boolean(resp?.variasFiliais),
      });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível gerar o relatório.'));
    } finally {
      setLoading(false);
    }
  }, [applied, page, permissions]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const aplicarFiltros = () => {
    setPage(1);
    setApplied({
      filialId,
      categoriaId,
      subCategoriaId,
      marcaId,
      termo: normalizeSearchTerm(termo),
    });
  };

  const exportarCsv = async () => {
    if (!applied) return;
    try {
      const { data: resp } = await getEstoqueDisponibilidadeRelatorio({
        estabelecimentoId: applied.filialId || undefined,
        categoriaId: applied.categoriaId || undefined,
        subCategoriaId: applied.subCategoriaId || undefined,
        marcaId: applied.marcaId || undefined,
        termo: applied.termo || undefined,
        formato: 'csv',
      });
      const blob = resp instanceof Blob ? resp : new Blob([resp], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'estoque_disponibilidade.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Falha ao exportar CSV.'));
    }
  };

  const accessors = useMemo(() => {
    const map = {};
    for (const local of data.locais || []) {
      map[`local-${local.id}`] = (row) => {
        const valor = row?.saldos?.[String(local.id)];
        return valor == null ? null : Number(valor);
      };
    }
    return map;
  }, [data.locais]);

  const { sorted: rowsOrdenados, orderBy, order, requestSort } = useClientSort(
    data.items || [],
    'descricao',
    'asc',
    accessors,
  );

  const totalPages = Math.max(1, Math.ceil((data.totalItems || 0) / PAGE));
  const colunasFixas = data.variasFiliais ? 7 : 6;
  const colSpan = colunasFixas + (data.locais?.length || 0) + 1;
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Disponibilidade de estoque"
        subtitle="Uma coluna por local e o total da filial. Produto sem saldo aparece zerado."
        actions={(
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportarCsv}
            disabled={!podeRelatorio(permissions) || !applied}
          >
            Exportar CSV
          </Button>
        )}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
            <TextField
              select
              label="Filial"
              value={filialId}
              onChange={(e) => setFilialId(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Todas permitidas</MenuItem>
              {filiais.map((f) => (
                <MenuItem key={f.id} value={String(f.id)}>{f.nome}</MenuItem>
              ))}
            </TextField>
            <BuscaProdutoCampo
              value={termo}
              onChange={setTermo}
              onSearch={aplicarFiltros}
              sx={{ minWidth: 280, flex: 1 }}
            />
            <Button variant="outlined" onClick={aplicarFiltros} sx={{ alignSelf: 'center' }}>
              Buscar
            </Button>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
            <TextField
              select
              label="Categoria"
              value={categoriaId}
              onChange={(e) => {
                setCategoriaId(e.target.value);
                setSubCategoriaId('');
              }}
              size="small"
              sx={{ minWidth: 180, flex: 1 }}
            >
              <MenuItem value="">Todas</MenuItem>
              {categorias.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>{c.descricao}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Subcategoria"
              value={subCategoriaId}
              onChange={(e) => setSubCategoriaId(e.target.value)}
              disabled={!categoriaId}
              size="small"
              sx={{ minWidth: 180, flex: 1 }}
            >
              <MenuItem value="">Todas</MenuItem>
              {subcategorias.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>{s.descricao}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Marca"
              value={marcaId}
              onChange={(e) => setMarcaId(e.target.value)}
              size="small"
              sx={{ minWidth: 180, flex: 1 }}
            >
              <MenuItem value="">Todas</MenuItem>
              {marcas.map((m) => (
                <MenuItem key={m.id} value={String(m.id)}>{m.descricao}</MenuItem>
              ))}
            </TextField>
            <Button variant="contained" onClick={aplicarFiltros} sx={{ alignSelf: 'center' }}>
              Gerar
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="codigo" label="Código" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="descricao" label="Produto" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="categoria" label="Categoria" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="subCategoria" label="Subcategoria" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="marca" label="Marca" orderBy={orderBy} order={order} onSort={requestSort} />
              {data.variasFiliais && (
                <SortableTh id="estabelecimentoNome" label="Filial" orderBy={orderBy} order={order} onSort={requestSort} />
              )}
              {(data.locais || []).map((local) => (
                <SortableTh
                  key={local.id}
                  id={`local-${local.id}`}
                  label={local.rotulo || local.nome}
                  orderBy={orderBy}
                  order={order}
                  onSort={requestSort}
                  align="right"
                />
              ))}
              <SortableTh id="total" label="Total" orderBy={orderBy} order={order} onSort={requestSort} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(!data.items || data.items.length === 0) && !loading ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center">
                  Nenhum produto para os filtros selecionados.
                </TableCell>
              </TableRow>
            ) : (
              rowsOrdenados.map((row) => (
                <TableRow key={`${row.produtoId}-${row.estabelecimentoId}`} hover>
                  <TableCell>{row.codigo}</TableCell>
                  <TableCell>{row.descricao}</TableCell>
                  <TableCell>{row.categoria}</TableCell>
                  <TableCell>{row.subCategoria}</TableCell>
                  <TableCell>{row.marca}</TableCell>
                  {data.variasFiliais && <TableCell>{row.estabelecimentoNome}</TableCell>}
                  {(data.locais || []).map((local) => (
                    <TableCell key={local.id} align="right">{textoSaldo(row, local)}</TableCell>
                  ))}
                  <TableCell align="right">{fmtQtd(row.total)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
          {data.totalItems} produto(s)
        </Box>
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
          />
        )}
      </Stack>
    </Box>
  );
}
